import { createPortal } from "react-dom";
// Visor universal de archivos — SOLO ver o descargar, nunca editar.
// Acepta una LISTA de archivos (carrusel): flechas ◀ ▶, teclado (← →) y
// swipe en móvil para pasar de archivo sin cerrar el visor. Los atajos los
// explica la Ayuda de cada vista que lo abre (`ATAJOS_VISOR`), no un tooltip.
// Decide el renderer por mime/extensión:
//   imagen/video/PDF/texto → nativo · Word → PDF convertido en el server
//   Excel/CSV → tabla (SheetJS) · otro → descarga
// PDF y Hoja se cargan lazy (React.lazy) para no engordar el bundle.
import { lazy, Suspense, useEffect, useRef, useState } from "react";

const VisorPdf = lazy(() => import("@/ui/VisorPdf"));
const VisorHoja = lazy(() => import("@/ui/VisorHoja"));

export type VisorTarget = {
  nombre: string;
  mime: string;
  /** URL del contenido tal cual (imagen, video, pdf, texto, hoja) */
  url: string;
  /** URL del PDF derivado (solo documentos Word) */
  previewUrl?: string;
};

type TipoRender =
  | "imagen"
  | "video"
  | "pdf"
  | "hoja"
  | "texto"
  | "office"
  | "descarga";

export function tipoRender(mime: string, nombre: string): TipoRender {
  const m = (mime || "").toLowerCase();
  const ext = (nombre.split(".").pop() || "").toLowerCase();
  if (m.startsWith("image/")) return "imagen";
  if (m.startsWith("video/")) return "video";
  if (m === "application/pdf" || ext === "pdf") return "pdf";
  if (
    m.includes("spreadsheetml") ||
    m === "application/vnd.ms-excel" ||
    m.includes("opendocument.spreadsheet") ||
    m === "text/csv" ||
    ["xlsx", "xls", "ods", "csv"].includes(ext)
  )
    return "hoja";
  if (
    m === "application/msword" ||
    m.includes("wordprocessingml") ||
    m.includes("opendocument.text") ||
    m.includes("rtf") ||
    ["doc", "docx", "odt", "rtf"].includes(ext)
  )
    return "office";
  if (
    m.startsWith("text/") ||
    m === "application/json" ||
    ["txt", "md", "log", "json", "xml"].includes(ext)
  )
    return "texto";
  return "descarga";
}

export default function VisorArchivo({
  targets,
  inicial = 0,
  onClose,
}: {
  targets: VisorTarget[];
  inicial?: number;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(
    Math.min(Math.max(inicial, 0), Math.max(targets.length - 1, 0)),
  );
  const touchX = useRef<number | null>(null);
  const target = targets[idx];
  const varios = targets.length > 1;

  const anterior = () => setIdx((i) => (i > 0 ? i - 1 : i));
  const siguiente = () => setIdx((i) => (i < targets.length - 1 ? i + 1 : i));

  // Teclado: Esc cierra; ← → navegan (nunca cierra por click afuera).
  // Con modificadores no son del carrusel (Ctrl+←/→ cambia de caso).
  //
  // ⚠️ En CAPTURA sobre window y con stopPropagation: el visor es la capa de
  // hasta arriba y esas tres teclas son SOLO suyas. Antes las oía también lo de
  // abajo: en «Revisar mi correo» ← → cambiaban además el correo (y el visor se
  // quedaba con los archivos del anterior), y Esc cerraba la ventana entera —
  // Radix escucha Esc en captura sobre `document`, que corre DESPUÉS de la
  // captura en `window`. Medido en producción el 2026-10-13.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.key !== "Escape" && e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.stopPropagation();
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") anterior();
      else siguiente();
    };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, targets.length]);

  if (!target) return null;
  const tipo = tipoRender(target.mime, target.nombre);

  const cargando = (
    <p className="text-sm text-zinc-500 p-8 text-center">Cargando vista previa…</p>
  );

  /* ⚠️ PORTAL AL BODY, no es adorno.
   *
   * El visor se monta dentro del árbol del caso, y ese árbol vive bajo el header
   * con `view-transition-name: ark-barra-arriba` (index.css). Esa propiedad crea
   * un **contexto de apilado** y un **bloque contenedor**: el `z-50` del visor
   * deja de competir contra el `z-30` del header —compite dentro de su burbuja—
   * y el `fixed inset-0` deja de medirse contra la ventana. Medido: el visor
   * arrancaba en `top: 16` y el header pintaba ENCIMA de su franja superior
   * (Joe: «no está ocupando toda la pantalla el visor»).
   *
   * Sacándolo al `body` desaparecen los dos problemas de un solo golpe. Es lo
   * que hace `ui/Modal` vía `Dialog.Portal`, y por eso los modales nunca
   * tuvieron esto.
   */
  return createPortal(
    <div
      data-visor-archivo
      className="fixed inset-0 z-50 bg-black/80 flex flex-col"
      onTouchStart={(e) => {
        touchX.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
        touchX.current = null;
        // Swipe horizontal franco (>60px) cambia de archivo.
        if (dx > 60) anterior();
        else if (dx < -60) siguiente();
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-ink-900 border-b border-ink-700">
        {/* El nombre completo, partido en renglones si es largo: antes se
            cortaba con «…» y el resto vivía en un tooltip (en el celular, nunca). */}
        <p className="flex-1 min-w-0 break-words [overflow-wrap:anywhere] text-sm text-zinc-200">
          {target.nombre}
        </p>
        {varios && (
          <span className="text-xs text-zinc-500 whitespace-nowrap font-mono">
            {idx + 1} / {targets.length}
          </span>
        )}
        <a
          href={target.url}
          download={target.nombre}
          className="btn-ghost text-xs whitespace-nowrap"
        >
          ↓ Descargar
        </a>
        <button
          onClick={onClose}
          className="text-zinc-400 hover:text-white px-2 text-lg leading-none"
          aria-label="Cerrar visor"
        >
          ×
        </button>
      </div>

      {/* Contenido + flechas del carrusel */}
      <div className="relative flex-1 min-h-0">
        {varios && idx > 0 && (
          <button
            onClick={anterior}
            aria-label="Archivo anterior"
            className="absolute left-1 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full
                       bg-ink-900/80 border border-ink-600 text-zinc-300 hover:text-white
                       hover:border-accent-500 flex items-center justify-center text-lg"
          >
            ◀
          </button>
        )}
        {varios && idx < targets.length - 1 && (
          <button
            onClick={siguiente}
            aria-label="Archivo siguiente"
            className="absolute right-1 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full
                       bg-ink-900/80 border border-ink-600 text-zinc-300 hover:text-white
                       hover:border-accent-500 flex items-center justify-center text-lg"
          >
            ▶
          </button>
        )}

        <div
          key={idx}
          className="h-full overflow-auto flex items-stretch justify-center"
        >
          {tipo === "imagen" && (
            <img
              src={target.url}
              alt={target.nombre}
              className="max-w-full max-h-full object-contain m-auto"
            />
          )}
          {tipo === "video" && (
            <video
              src={target.url}
              controls
              className="max-w-full max-h-full m-auto"
            />
          )}
          {tipo === "pdf" && (
            <Suspense fallback={cargando}>
              <VisorPdf url={target.url} nombre={target.nombre} />
            </Suspense>
          )}
          {tipo === "office" && (
            <Suspense fallback={cargando}>
              <VisorPdf
                url={target.previewUrl || target.url}
                nombre={target.nombre}
                notaConversion
              />
            </Suspense>
          )}
          {tipo === "hoja" && (
            <Suspense fallback={cargando}>
              <VisorHoja url={target.url} nombre={target.nombre} />
            </Suspense>
          )}
          {tipo === "texto" && <VisorTexto url={target.url} />}
          {tipo === "descarga" && (
            <div className="m-auto text-center space-y-3 p-8">
              <span className="text-5xl">📎</span>
              <p className="text-sm text-zinc-400">
                Este tipo de archivo no tiene vista previa.
              </p>
              <a
                href={target.url}
                download={target.nombre}
                className="btn-primary text-sm"
              >
                ↓ Descargar {target.nombre}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function VisorTexto({ url }: { url: string }) {
  const [texto, setTexto] = useState<string | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setTexto(null);
    setError(false);
    fetch(url, { credentials: "include" })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(r.statusText))))
      .then(setTexto)
      .catch(() => setError(true));
  }, [url]);
  if (error)
    return (
      <p className="m-auto text-sm text-red-400">No se pudo cargar el archivo.</p>
    );
  if (texto === null)
    return <p className="m-auto text-sm text-zinc-500">Cargando…</p>;
  return (
    <pre className="w-full max-w-4xl m-auto p-4 text-xs text-zinc-300 whitespace-pre-wrap break-words">
      {texto}
    </pre>
  );
}
